import { createTheme, Palette, PaletteOptions, Theme, ThemeOptions } from "@mui/material/styles";
import { deepmerge } from "@mui/utils";

import { OrganizationKey } from "../utils/orgConfig";
import { LKC_COLORS, OVERRIDE_SELECTORS, PAS_COLORS, UNIT_USAGE_SELECTORS } from "./asdColors";
import { baseTheme, OverrideKey, UnitUsageKey } from "./baseTheme";

const ORGANIZATION_COLORS = {
  LKC: LKC_COLORS,
  PAS: PAS_COLORS,
} as const;

export type ThemeMode = "light" | "dark";
const MODES: ThemeMode[] = ["light", "dark"];

const createAppTheme = (organization: OrganizationKey, mode: ThemeMode, colors: PaletteOptions): Theme => {
  const baseColorTheme = createTheme({ palette: { mode, ...colors } });

  const custom = baseColorTheme.palette.augmentColor({
    color: colors.custom as { main: string },
    name: "custom",
  });

  const withCustom = createTheme({ palette: { mode, ...colors, custom } });

  const overrideSelectors = OVERRIDE_SELECTORS[organization];
  const overrides = Object.fromEntries(
    (Object.entries(overrideSelectors) as [OverrideKey, (palette: Palette) => string][]).map(([key, selector]) => [
      key,
      { main: selector(withCustom.palette) },
    ]),
  ) as Record<OverrideKey, { main: string }>;

  const unitUsageSelectors = UNIT_USAGE_SELECTORS[organization];
  const unitUsage = Object.fromEntries(
    (Object.entries(unitUsageSelectors) as [UnitUsageKey, (palette: Palette) => string][]).map(([key, selector]) => [
      key,
      selector(withCustom.palette),
    ]),
  ) as Record<UnitUsageKey, string>;

  return createTheme(
    deepmerge(baseTheme, {
      organization,
      palette: {
        mode,
        ...colors,
        custom,
        overrides,
        unitUsage,
      },
    }) as ThemeOptions,
  );
};

export const THEMES = Object.fromEntries(
  (Object.entries(ORGANIZATION_COLORS) as [OrganizationKey, PaletteOptions][]).map(([organization, colors]) => [
    organization,
    Object.fromEntries(MODES.map((mode) => [mode, createAppTheme(organization, mode, colors)])) as Record<ThemeMode, Theme>,
  ]),
) as Record<OrganizationKey, Record<ThemeMode, Theme>>;
